import { useState, useEffect } from "react";
import { Bell, Clock, CheckCircle, XCircle, Wrench } from "lucide-react";
import { useUserData } from "../hooks/useUserData";
import { getStudentNotifications, getAdminNotifications, getTechnicianNotifications, markNotificationAsRead } from "../services/notifications";
import { useNavigate } from "react-router-dom";

export default function NotificationBell() {
  const [user, userLoading] = useUserData();
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  // Load notifications based on user role
  useEffect(() => {
    // Only proceed if user is fully loaded and has a valid role
    if (!user || userLoading || !user.role || (user.role !== "admin" && user.role !== "student" && user.role !== "technician")) return;

    let unsubscribe;

    const loadNotifications = async () => {
      try {
        if (user.role === "student") {
          console.log("Loading student notifications for user:", user.uid);
          const callback = (studentNotifications) => {
            console.log("Received student notifications:", studentNotifications.length, studentNotifications);
            setNotifications(studentNotifications);
          };
          unsubscribe = getStudentNotifications(user.uid, callback);
        } else if (user.role === "admin") {
          const callback = (adminNotifications) => {
            setNotifications(adminNotifications);
          };
          unsubscribe = getAdminNotifications(callback);
        } else if (user.role === "technician") {
          console.log("Loading technician notifications for user:", user.uid);
          const callback = (technicianNotifications) => {
            setNotifications(technicianNotifications);
          };
          unsubscribe = getTechnicianNotifications(user.uid, callback);
        }
      } catch (error) {
        console.error("Error loading notifications:", error);
      }
    };

    loadNotifications();

    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        try {
          unsubscribe();
        } catch (unsubscribeError) {
          console.warn("Error unsubscribing from notifications:", unsubscribeError);
        }
      }
    };
  }, [user, userLoading]);

  const handleNotificationClick = async (notification) => {
    // Mark as read for all notification types
    if (user && user.role === "admin") {
      await markNotificationAsRead(notification.id);
    } else if (user && user.role === "student") {
      await markNotificationAsRead(notification.id, "scheduledNotifications");
    } else if (user && user.role === "technician") {
      // For technicians, mark the notification as read in the task document
      try {
        await markNotificationAsRead(notification.id, "maintenanceTasks");
      } catch (error) {
        console.error("Error marking technician notification as read:", error);
      }
    }

    // Navigate based on notification type
    if (notification.type === "borrow_request" || notification.type === "asset_return" || notification.type === "overdue_asset" || notification.type === "request_approved") {
      if (user?.role === "admin") {
        navigate("/admin/requests");
      } else {
        navigate("/requests");
      }
    } else if (notification.type === "return_reminder") {
      navigate("/scanner");
    } else if (notification.type === "maintenance_task") {
      if (user?.role === "technician") {
        navigate("/technician/tasks");
      } else {
        navigate(`/technician/task/${notification.id}`);
      }
    }

    setIsOpen(false);
  };

  const handleViewAllNotifications = () => {
    if (user?.role === "student") {
      navigate("/notifications");
    } else if (user?.role === "admin") {
      navigate("/admin/notifications");
    } else if (user?.role === "technician") {
      navigate("/technician/notifications");
    }
    setIsOpen(false);
  };

  const handleMarkAllRead = async () => {
    for (const notification of notifications) {
      if (user && user.role === "admin" && notification.status !== 'read') {
        await markNotificationAsRead(notification.id);
      } else if (user && user.role === "student" && notification.status !== 'read') {
        await markNotificationAsRead(notification.id, "scheduledNotifications");
      } else if (user && user.role === "technician" && notification.status !== 'read') {
        await markNotificationAsRead(notification.id, "maintenanceTasks");
      }
    }
  };

  const unreadCount = notifications.filter(n => n.status !== 'read').length;
  const hasUnread = unreadCount > 0;

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`glass-button p-2 rounded-lg hover:bg-surface-200 transition-all duration-200 active:scale-95 relative ${
          hasUnread ? 'ring-2 ring-orange-400 animate-pulse' : ''
        }`}
      >
        <Bell className={`w-5 h-5 text-neutral-700 ${hasUnread ? 'text-orange-600' : ''}`} />
        {hasUnread && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-semibold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 z-30"
            onClick={() => setIsOpen(false)}
          />

          {/* Notifications Panel */}
          <div className="absolute right-0 top-12 w-80 max-w-sm bg-white rounded-xl shadow-lg border border-neutral-200 z-40 max-h-96">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-neutral-100">
              <h3 className="font-semibold text-neutral-900">Notifications</h3>
              {notifications.length > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs text-orange-600 hover:text-orange-700 font-medium"
                >
                  Mark all read
                </button>
              )}
            </div>

            {/* Items */}
            <div className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-neutral-500">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No new notifications</p>
                </div>
              ) : (
                notifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 border-b border-neutral-100 hover:bg-neutral-50 cursor-pointer transition-colors ${
                      notification.status === 'read' ? 'opacity-60' : ''
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        {notification.type === "borrow_request" && (
                          <div className={`w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center ${
                            notification.status === 'read' ? 'bg-blue-50' : ''
                          }`}>
                            <Clock className={`w-4 h-4 text-blue-600 ${
                              notification.status === 'read' ? 'text-blue-400' : ''
                            }`} />
                          </div>
                        )}
                        {(notification.type === "asset_return" || notification.type === "request_approved") && (
                          <div className={`w-8 h-8 bg-green-100 rounded-full flex items-center justify-center ${
                            notification.status === 'read' ? 'bg-green-50' : ''
                          }`}>
                            <CheckCircle className={`w-4 h-4 text-green-600 ${
                              notification.status === 'read' ? 'text-green-400' : ''
                            }`} />
                          </div>
                        )}
                        {(notification.type === "overdue_asset" || notification.type === "return_reminder") && (
                          <div className={`w-8 h-8 bg-red-100 rounded-full flex items-center justify-center ${
                            notification.status === 'read' ? 'bg-red-50' : ''
                          }`}>
                            <XCircle className={`w-4 h-4 text-red-600 ${
                              notification.status === 'read' ? 'text-red-400' : ''
                            }`} />
                          </div>
                        )}
                        {notification.type === "maintenance_task" && (
                          <div className={`w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center ${
                            notification.status === 'read' ? 'bg-orange-50' : ''
                          }`}>
                            <Wrench className={`w-4 h-4 text-orange-600 ${
                              notification.status === 'read' ? 'text-orange-400' : ''
                            }`} />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${
                          notification.status === 'read' ? 'text-neutral-600' : 'text-neutral-900'
                        }`}>
                          {notification.type === "borrow_request" && "New Borrow Request"}
                          {notification.type === "asset_return" && "Asset Returned"}
                          {notification.type === "request_approved" && "Request Approved"}
                          {notification.type === "overdue_asset" && "Overdue Asset"}
                          {notification.type === "return_reminder" && "Return Reminder"}
                          {notification.type === "maintenance_task" && "Maintenance Task"}
                        </p>
                        <p className={`text-sm mt-1 ${
                          notification.status === 'read' ? 'text-neutral-500' : 'text-neutral-600'
                        }`}>
                          {notification.message}
                        </p>
                        <p className="text-xs text-neutral-400 mt-1">
                          {notification.createdAt?.toDate ?
                            notification.createdAt.toDate().toLocaleString() :
                            'Just now'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-neutral-100 space-y-2">
              {notifications.length > 0 && (
                <button
                  onClick={handleViewAllNotifications}
                  className="w-full text-center text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  View All Notifications
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="w-full text-center text-sm text-neutral-600 hover:text-neutral-800"
              >
                Close
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
